/**
 * BaseWeaponComponent - common weapon functionality including visual representation,
 * sound management, ammo system, crosshair support, and recoil effects.
 * Shared functionality between projectile weapons and instant hit weapons.
 * Extends GenericEquipment for timing logic on fire actions.
 */
import * as THREE from 'three';

import { Crosshair } from '../../ui/components/Crosshair.js';
import { Delegate } from '../../utils/Delegate.js';
import { property } from '../../utils/serialization/decorator.js';
import { SoundComponent } from '../audio/SoundComponent.js';
import { SoundResource } from '../audio/SoundResource.js';
import { ModelMeshComponent } from '../visual/ModelMeshComponent.js';

import { GenericEquipment } from './GenericEquipment.js';

import type { IEquipment } from './Equipment.js';
import type { GenericEquipmentOptions } from './GenericEquipment.js';
import type { CrosshairOptions } from '../../ui/components/Crosshair.js';
import type { AudioPath, ModelPath } from '../../utils/serialization/decorator.js';

export interface RecoilOptions {
  weaponRecoilDistance: number;
  weaponRecoilDuration: number;
  weaponRecoilRotation: number;
}

export interface BaseWeaponOptions extends GenericEquipmentOptions {
  modelPath?: string;
  modelTransform?: {
    position?: THREE.Vector3;
    rotation?: THREE.Euler;
    scale?: THREE.Vector3;
  };
  fireSpawnOffset?: THREE.Vector3;
  crosshairOptions?: CrosshairOptions;
  ammoCapacity?: number;
  reloadTime?: number;
  fireSoundUrl?: string;
  emptyFireSoundUrl?: string;
  reloadSoundUrl?: string;
  recoilOptions?: RecoilOptions;
  range?: number;
}

export type ReloadStartedDelegate = Delegate<[], void>;
export type ReloadCompletedDelegate = Delegate<[], void>;

enum WeaponSounds {
  Fire = 'fire',
  EmptyFire = 'emptyFire',
  Reload = 'reload',
}


export abstract class BaseWeaponComponent extends GenericEquipment implements IEquipment {

  protected targetPosition: THREE.Vector3 | null = null;
  protected weaponMesh: THREE.Object3D | null = null;
  protected crosshair: Crosshair | null = null;

  // Ammo management
  protected currentAmmo: number = 0;
  protected isReloading: boolean = false;
  protected raycaster: THREE.Raycaster | null = null;
  protected soundComponent: SoundComponent | null = null;

  // Recoil effect properties
  protected originalWeaponPosition: THREE.Vector3 = new THREE.Vector3();
  protected originalWeaponRotation: THREE.Euler = new THREE.Euler();

  // Reload animation properties
  protected reloadAnimationTimer: any = null;

  // Options stored as properties
  @property()
  protected modelPath?: ModelPath;

  protected modelTransform?: {
    position?: THREE.Vector3;
    rotation?: THREE.Euler;
    scale?: THREE.Vector3;
  };

  protected fireSpawnOffset?: THREE.Vector3;
  protected crosshairOptions?: CrosshairOptions;

  @property({ type: 'number', min: 0, step: 1, decimals: 0 })
  protected ammoCapacity: number = 0;

  @property({ type: 'number', min: 0, step: 0.1, decimals: 1 })
  protected reloadTime: number = 2.0;

  @property()
  protected fireSoundUrl?: AudioPath;

  @property()
  protected emptyFireSoundUrl?: AudioPath;

  @property()
  protected reloadSoundUrl?: AudioPath;

  protected recoilOptions: RecoilOptions = {
    weaponRecoilDistance: 0.1,
    weaponRecoilDuration: 0.15,
    weaponRecoilRotation: 0.05,
  };

  @property({ type: 'number', min: 0, step: 10, decimals: 0 })
  protected range: number = 1000;

  public readonly onReloadStarted: ReloadStartedDelegate = new Delegate<[], void>();
  public readonly onReloadCompleted: ReloadCompletedDelegate = new Delegate<[], void>();

  public override initialize(options?: BaseWeaponOptions): void {
    super.initialize(options);
    this.modelPath = options?.modelPath ?? this.modelPath;
    this.modelTransform = options?.modelTransform ?? this.modelTransform;
    this.fireSpawnOffset = options?.fireSpawnOffset ?? this.fireSpawnOffset;
    this.crosshairOptions = options?.crosshairOptions ?? this.crosshairOptions;
    this.ammoCapacity = options?.ammoCapacity ?? this.ammoCapacity;
    this.reloadTime = options?.reloadTime ?? this.reloadTime;
    this.fireSoundUrl = options?.fireSoundUrl ?? this.fireSoundUrl;
    this.emptyFireSoundUrl = options?.emptyFireSoundUrl ?? this.emptyFireSoundUrl;
    this.reloadSoundUrl = options?.reloadSoundUrl ?? this.reloadSoundUrl;
    this.recoilOptions = options?.recoilOptions ?? this.recoilOptions;
    this.range = options?.range ?? this.range;

    // Initialize ammo based on capacity
    this.currentAmmo = this.ammoCapacity;

    // Create the weapon visual representation
    this.createWeaponMesh();
  }

  public override beginPlay(): void {
    super.beginPlay();

    const owner = this.getActor();
    if (owner?.getPlayerController() && this.crosshairOptions) {
      this.crosshair = new Crosshair(this.getWorld()!.uiManager, this.crosshairOptions);
      this.crosshair.initialize();
    }

    // Setup sound component with all weapon sounds
    const soundResources: SoundResource[] = [];
    if (this.fireSoundUrl) {
      const fireSound = new SoundResource();
      fireSound.name = WeaponSounds.Fire;
      fireSound.audioPath = this.fireSoundUrl;
      soundResources.push(fireSound);
    }
    if (this.emptyFireSoundUrl) {
      const emptySound = new SoundResource();
      emptySound.name = WeaponSounds.EmptyFire;
      emptySound.audioPath = this.emptyFireSoundUrl;
      soundResources.push(emptySound);
    }
    if (this.reloadSoundUrl) {
      const reloadSound = new SoundResource();
      reloadSound.name = WeaponSounds.Reload;
      reloadSound.audioPath = this.reloadSoundUrl;
      soundResources.push(reloadSound);
    }

    if (soundResources.length > 0) {
      this.soundComponent = SoundComponent.create({
        sounds: soundResources,
      });
      this.add(this.soundComponent);
    }

    // Store original weapon transform for recoil reset
    if (this.weaponMesh) {
      this.originalWeaponPosition.copy(this.weaponMesh.position);
      this.originalWeaponRotation.copy(this.weaponMesh.rotation);
    }
  }

  public override endPlay(): void {
    if (this.crosshair) {
      this.crosshair.destroy();
      this.crosshair = null;
    }

    // Cancel any ongoing reload animation
    if (this.reloadAnimationTimer) {
      const world = this.getWorld();
      if (world) {
        world.timerSystem.clearTimer(this.reloadAnimationTimer);
      }
      this.reloadAnimationTimer = null;
    }

    super.endPlay();
  }

  protected createWeaponMesh(): void {
    if (this.modelPath) {
      // Load 3D model using ModelMeshComponent
      console.log(`Loading weapon model: ${this.modelPath}`);
      const gltfComponent = ModelMeshComponent.create({
        modelUrl: this.modelPath,
        position: this.modelTransform?.position,
        rotation: this.modelTransform?.rotation,
        scale: this.modelTransform?.scale,
        physicsOptions: {
          enabled: false, // Disable physics for weapon visuals
        }
      });
      this.weaponMesh = gltfComponent;
      this.add(this.weaponMesh);
    }
  }

  public tryFireAtTarget(targetPosition: THREE.Vector3): void {
    this.targetPosition = targetPosition;
    this.beginFire();
    this.endFire();
    this.targetPosition = null;
  }

  /**
   * Override to add custom firing conditions.
   * @returns true if the weapon can fire
   */
  protected canFire(): boolean {
    // Check if reloading
    if (this.isReloading) {
      return false;
    }

    // Check ammo if not infinite
    if (this.ammoCapacity > 0 && this.currentAmmo <= 0) {
      return false;
    }

    return true;
  }

  /**
   * Override to add custom reload conditions.
   * @returns true if the weapon can reload
   */
  protected canReload(): boolean {
    // Can't reload infinite ammo weapons
    if (this.ammoCapacity <= 0) {
      return false;
    }

    // Can't reload if already reloading
    if (this.isReloading) {
      return false;
    }

    // Can't reload if already at full capacity
    if (this.currentAmmo >= this.ammoCapacity) {
      return false;
    }

    return true;
  }

  // Ammo management methods
  public override beginFire(): boolean {
    if (!this.canFire()) {
      // Play empty fire sound if ammo is depleted
      if (this.ammoCapacity > 0 && this.currentAmmo <= 0 && this.soundComponent) {
        this.soundComponent.play(WeaponSounds.EmptyFire, 1.0, true);
      }
      return false;
    }

    return super.beginFire();
  }

  public getCurrentAmmo(): number {
    return this.currentAmmo;
  }

  public getMaxAmmo(): number {
    return this.ammoCapacity;
  }

  public isAmmoInfinite(): boolean {
    return this.ammoCapacity <= 0;
  }

  /**
   * Sets the current ammo count directly without reloading.
   * @param ammo - the new ammo count
   */
  public setCurrentAmmo(ammo: number): void {
    if (this.ammoCapacity > 0) {
      this.currentAmmo = Math.max(0, Math.min(ammo, this.ammoCapacity));
    }
  }

  /**
   * Implements IEquipment.reload() interface method.
   * @returns true if reload was initiated
   */
  public override reload(): boolean {
    if (this.canReload()) {
      this.startReload();
      return true;
    }
    return false;
  }

  protected startReload(): void {
    if (!this.canReload()) {
      return;
    }

    this.isReloading = true;
    console.log('Reloading weapon...');

    // Emit reload started event
    this.onReloadStarted.invoke();

    // Play reload sound
    if (this.soundComponent) {
      this.soundComponent.play(WeaponSounds.Reload, 1.0, true);
    }

    // Start weapon rotation animation during reload
    this.startReloadAnimation();

    // Use timer system for reload
    const world = this.getWorld();
    if (world) {
      world.timerSystem.setTimeout(() => {
        this.currentAmmo = this.ammoCapacity;
        this.isReloading = false;
        console.log(`Reload complete - ${this.ammoCapacity} rounds ready`);

        // Emit reload completed event
        this.onReloadCompleted.invoke();
      }, this.reloadTime);
    }
  }

  protected startReloadAnimation(): void {
    if (!this.weaponMesh) {
      return;
    }

    const world = this.getWorld();
    if (!world) {
      return;
    }

    // Cancel any existing reload animation
    if (this.reloadAnimationTimer) {
      world.timerSystem.clearTimer(this.reloadAnimationTimer);
    }

    const reloadDuration = this.reloadTime;
    const startTime = world.getGameTime();

    this.reloadAnimationTimer = world.timerSystem.setInterval(() => {
      if (!this.weaponMesh || !this.isReloading) {
        if (this.reloadAnimationTimer) {
          world.timerSystem.clearTimer(this.reloadAnimationTimer);
          this.reloadAnimationTimer = null;
        }
        return;
      }

      const currentTime = world.getGameTime();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / reloadDuration, 1);

      // Create a smooth rotation animation - rotate around Y axis (spin)
      const rotationAmount = Math.sin(progress * Math.PI * 2) * 0.3; // Oscillating rotation
      this.weaponMesh.rotation.y = this.originalWeaponRotation.y + rotationAmount;

      // Also add a slight tilt animation
      const tiltAmount = Math.sin(progress * Math.PI * 4) * 0.1; // Faster oscillation for tilt
      this.weaponMesh.rotation.z = this.originalWeaponRotation.z + tiltAmount;

      // Clean up when reload is complete
      if (progress >= 1 || !this.isReloading) {
        // Reset weapon to original rotation
        this.weaponMesh.rotation.copy(this.originalWeaponRotation);
        world.timerSystem.clearTimer(this.reloadAnimationTimer);
        this.reloadAnimationTimer = null;
      }
    }, 1 / 60); // 60 FPS animation
  }

  /**
   * Gets the target location by shooting a ray from the center of the camera.
   * @param maxDistance - maximum distance for the raycast
   * @returns the world position where the ray intersects, or null if no intersection
   */
  public getTargetLocationFromCamera(maxDistance: number): THREE.Vector3 | null {
    const world = this.getWorld();
    const actor = this.getActor();
    if (!world || !actor) {
      return null;
    }

    // Get the active camera
    const camera = actor.getComponent(THREE.Camera);
    if (!camera) {
      return null;
    }

    const cameraPosition = camera.getWorldPosition();
    const cameraDirection = new THREE.Vector3(0, 0, 0);
    camera.getWorldDirection(cameraDirection);

    const hitTestResult = this.getPhysicsEngine()?.performHitTest({
      origin: cameraPosition,
      direction: cameraDirection,
      maxDistance: maxDistance,
      stopOnFirstHit: true,
      ignoredActors: [actor],
    });

    return hitTestResult?.[0]?.hitLocation ?? null;
  }

  protected applyRecoilEffects(): void {
    const world = this.getWorld();
    const actor = this.getActor();

    if (!world || !actor) return;

    // Apply weapon model recoil (can overlap for rapid fire effect)
    this.applyWeaponRecoil(this.recoilOptions);
  }

  protected applyWeaponRecoil(recoilOptions: RecoilOptions): void {
    if (!this.weaponMesh) {
      return;
    }

    const recoilDistance = recoilOptions.weaponRecoilDistance;
    const recoilRotation = recoilOptions.weaponRecoilRotation;
    const duration = recoilOptions.weaponRecoilDuration;
    const world = this.getWorld();

    if (!world || recoilDistance <= 0) {
      return;
    }

    // Get current weapon position (might be mid-recoil from previous shot)
    const currentPosition = this.weaponMesh.position.clone();
    const currentRotation = this.weaponMesh.rotation.clone();

    // Calculate recoil target position (move backward along local Z axis)
    const recoilTargetPosition = this.originalWeaponPosition.clone();
    recoilTargetPosition.z += recoilDistance;

    // Calculate recoil target rotation (pitch up)
    const recoilTargetRotation = this.originalWeaponRotation.clone();
    recoilTargetRotation.x -= recoilRotation;

    // Use faster animation for rapid fire (minimum 30fps for smoothness)
    const minStepDuration = 1 / 30;
    const animationSteps = Math.max(Math.floor(duration * 60), 2); // At least 2 steps
    const stepDuration = Math.max(duration / animationSteps, minStepDuration);
    let currentStep = 0;

    const recoilTimer = world.timerSystem.setInterval(() => {
      currentStep++;
      const progress = currentStep / animationSteps;

      if (progress <= 0.4) {
        // First 40% of animation: quick snap to recoil position
        const recoilProgress = progress / 0.4;
        // Use ease-out for snappy recoil
        const easedProgress = 1 - Math.pow(1 - recoilProgress, 2);
        this.weaponMesh!.position.lerpVectors(currentPosition, recoilTargetPosition, easedProgress);
        this.weaponMesh!.rotation.x = THREE.MathUtils.lerp(currentRotation.x, recoilTargetRotation.x, easedProgress);
      } else {
        // Remaining 60% of animation: smooth return to original position
        const returnProgress = (progress - 0.4) / 0.6;
        // Use ease-in for smooth return
        const easedProgress = returnProgress * returnProgress;
        this.weaponMesh!.position.lerpVectors(recoilTargetPosition, this.originalWeaponPosition, easedProgress);
        this.weaponMesh!.rotation.x = THREE.MathUtils.lerp(recoilTargetRotation.x, this.originalWeaponRotation.x, easedProgress);
      }

      // Stop animation when complete
      if (progress >= 1) {
        world.timerSystem.clearTimer(recoilTimer);
        // Ensure weapon is back to original position
        this.weaponMesh!.position.copy(this.originalWeaponPosition);
        this.weaponMesh!.rotation.copy(this.originalWeaponRotation);
      }
    }, stepDuration);
  }

  protected consumeAmmo(): void {
    // Consume ammo if not infinite
    if (this.ammoCapacity > 0) {
      this.currentAmmo--;

      if (this.currentAmmo <= 0) {
        this.startReload();
      }
    }
  }

  protected playFireSound(): void {
    if (this.soundComponent) {
      this.soundComponent.play(WeaponSounds.Fire, 1.0, true);
    }
  }

  /**
   * Gets the fire position and direction for this weapon.
   * Override to customize spawn point offset from the component's transform.
   */
  protected getFirePositionAndDirection(): { position: THREE.Vector3, direction: THREE.Vector3 } {
    const worldPosition = new THREE.Vector3();
    const worldDirection = new THREE.Vector3();
    this.getWorldPosition(worldPosition);

    const camera = this.getActor()?.getComponent(THREE.Camera);
    if (camera) {
      camera.getWorldDirection(worldDirection);
    } else {
      this.getWorldDirection(worldDirection);
    }

    if (this.fireSpawnOffset) {
      worldPosition.add(this.fireSpawnOffset.clone().applyQuaternion(this.getWorldQuaternion()));
    }
    return { position: worldPosition, direction: worldDirection };
  }

  protected getTargetDirection(): THREE.Vector3 | null {
    const { position } = this.getFirePositionAndDirection();
    const targetPosition = this.targetPosition ?? this.getTargetLocationFromCamera(this.range);
    if (targetPosition) {
      return targetPosition.clone().sub(position).normalize();
    }
    return null;
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Weapon';
  }
}